import { useId, useMemo, useState } from 'react'
import { useTranslation } from '../../lib/i18n'
import { computeRoi, WEEKS_PER_MONTH } from '../../lib/automation-roi'
import type { RoiInput } from '../../lib/automation-roi'

/**
 * Fully client-side AI Automation ROI calculator.
 *
 * Pure arithmetic over the shared formula module
 * ({@link ../../lib/automation-roi.ts}): enter how much time a recurring task
 * eats today (hours/week, people, hourly cost), how much of it an automation can
 * take over, plus the one-time setup and the recurring tool cost, and the widget
 * shows time saved per month/year, gross and net monthly saving, first-year and
 * steady-state annual saving, the payback period and the first-year ROI. Nothing
 * leaves the browser: no sign-up, no API, no per-use cost.
 *
 * SSR-safe: the default state is deterministic and identical on server and
 * client (no Date.now/Math.random in render). Stable ids come from useId. All UI
 * copy is read from the dictionary so it renders natively in EN and DE; money is
 * formatted with Intl in the active locale. Mounted by the tool detail page via
 * the `ai-automation-roi-calculator` toolKey.
 */

/** Currency per locale: USD for EN, CHF for DE-CH and EUR for FR/IT. */
function currencyForLocale(locale: string): string {
  if (locale.startsWith('de')) return 'CHF'
  if (locale.startsWith('fr') || locale.startsWith('it')) return 'EUR'
  return 'USD'
}

// Deterministic, sensible defaults so server and client render identically and a
// first-time visitor immediately sees a meaningful, positive result.
const DEFAULTS: RoiInput = {
  hoursPerWeek: 5,
  people: 3,
  hourlyCost: 60,
  automatablePercent: 70,
  setupHours: 20,
  setupCost: 0,
  monthlyToolCost: 50,
}

/** Parse a numeric field defensively; never returns NaN or a negative value. */
function parseAmount(raw: string): number {
  const n = Number.parseFloat(raw)
  if (!Number.isFinite(n) || n < 0) return 0
  return n
}

export function AutomationRoiCalculator() {
  const { dict, locale, path } = useTranslation()
  const ui = dict.pages.toolWidgets.roiCalculator

  const [hoursPerWeek, setHoursPerWeek] = useState(String(DEFAULTS.hoursPerWeek))
  const [people, setPeople] = useState(String(DEFAULTS.people))
  const [hourlyCost, setHourlyCost] = useState(String(DEFAULTS.hourlyCost))
  const [automatablePercent, setAutomatablePercent] = useState(
    String(DEFAULTS.automatablePercent),
  )
  const [setupHours, setSetupHours] = useState(String(DEFAULTS.setupHours))
  const [setupCost, setSetupCost] = useState(String(DEFAULTS.setupCost))
  const [monthlyToolCost, setMonthlyToolCost] = useState(
    String(DEFAULTS.monthlyToolCost),
  )

  const baseId = useId()

  const currencyCode = currencyForLocale(locale)
  const money = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 0,
      }),
    [locale, currencyCode],
  )
  const number = useMemo(
    () => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }),
    [locale],
  )
  const oneDecimal = useMemo(
    () => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }),
    [locale],
  )
  const percent = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'percent',
        maximumFractionDigits: 0,
      }),
    [locale],
  )

  // Pure arithmetic over the shared formula; cheap enough to run every render.
  const result = computeRoi({
    hoursPerWeek: parseAmount(hoursPerWeek),
    people: parseAmount(people),
    hourlyCost: parseAmount(hourlyCost),
    automatablePercent: parseAmount(automatablePercent),
    setupHours: parseAmount(setupHours),
    setupCost: parseAmount(setupCost),
    monthlyToolCost: parseAmount(monthlyToolCost),
  })

  const pays = result.netMonthlySaving > 0
  const guideHref = path('guide', { guideSlug: 'ai-automation-for-business' })
  const comparisonHref = path('comparison', {
    comparisonSlug: 'n8n-vs-zapier-vs-make',
  })
  const glossaryHref = path('glossaryTerm', { termSlug: 'workflow-automation' })

  const paybackText =
    result.paybackMonths === null
      ? ui.noPayback
      : ui.paybackValue.replace(
          '{months}',
          oneDecimal.format(result.paybackMonths),
        )
  const roiText =
    result.roiFraction === null ? ui.notAvailable : percent.format(result.roiFraction)

  const fields: Array<{
    key: string
    label: string
    hint?: string
    value: string
    onChange: (v: string) => void
    step: number
    max?: number
  }> = [
    {
      key: 'hours',
      label: ui.hoursLabel,
      value: hoursPerWeek,
      onChange: setHoursPerWeek,
      step: 1,
    },
    {
      key: 'people',
      label: ui.peopleLabel,
      value: people,
      onChange: setPeople,
      step: 1,
    },
    {
      key: 'cost',
      label: ui.hourlyCostLabel.replace('{currency}', currencyCode),
      value: hourlyCost,
      onChange: setHourlyCost,
      step: 5,
    },
    {
      key: 'auto',
      label: ui.automatableLabel,
      hint: ui.automatableHint,
      value: automatablePercent,
      onChange: setAutomatablePercent,
      step: 5,
      max: 100,
    },
    {
      key: 'setupHours',
      label: ui.setupHoursLabel,
      value: setupHours,
      onChange: setSetupHours,
      step: 1,
    },
    {
      key: 'setupCost',
      label: ui.setupCostLabel.replace('{currency}', currencyCode),
      value: setupCost,
      onChange: setSetupCost,
      step: 50,
    },
    {
      key: 'toolCost',
      label: ui.monthlyToolCostLabel.replace('{currency}', currencyCode),
      value: monthlyToolCost,
      onChange: setMonthlyToolCost,
      step: 10,
    },
  ]

  return (
    <div className="tool-widget">
      <form className="tool-widget-form" onSubmit={(e) => e.preventDefault()}>
        <div className="tool-field-grid">
          {fields.map((field) => {
            const id = `${baseId}-${field.key}`
            return (
              <label className="tool-field" htmlFor={id} key={field.key}>
                <span>{field.label}</span>
                <input
                  id={id}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={field.max}
                  step={field.step}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                />
                {field.hint ? (
                  <small className="tool-field-hint">{field.hint}</small>
                ) : null}
              </label>
            )
          })}
        </div>
      </form>

      <div
        className={`tool-result-panel${pays ? '' : ' is-negative'}`}
        aria-live="polite"
      >
        <div className="tool-result-headline">
          <span className="tool-result-headline-label">
            {pays ? ui.netMonthlyLabel : ui.netMonthlyLossLabel}
          </span>
          <span className="tool-result-headline-value">
            {money.format(Math.abs(result.netMonthlySaving))}
          </span>
        </div>

        <dl className="tool-result-grid">
          <div className="tool-result-cell">
            <dt>{ui.hoursSavedLabel}</dt>
            <dd>
              {number.format(Math.round(result.hoursSavedPerMonth))}
              <small>{ui.perMonthUnit}</small>
            </dd>
          </div>
          <div className="tool-result-cell">
            <dt>{ui.hoursSavedYearLabel}</dt>
            <dd>
              {number.format(Math.round(result.hoursSavedPerYear))}
              <small>{ui.perYearUnit}</small>
            </dd>
          </div>
          <div className="tool-result-cell">
            <dt>{ui.grossMonthlyLabel}</dt>
            <dd>{money.format(result.grossMonthlySaving)}</dd>
          </div>
          <div className="tool-result-cell">
            <dt>{ui.annualNetLabel}</dt>
            <dd>{money.format(result.annualNetSaving)}</dd>
          </div>
          <div className="tool-result-cell">
            <dt>{ui.setupInvestmentLabel}</dt>
            <dd>{money.format(result.totalSetupInvestment)}</dd>
          </div>
          <div className="tool-result-cell">
            <dt>{ui.firstYearNetLabel}</dt>
            <dd>{money.format(result.firstYearNetSaving)}</dd>
          </div>
          <div className="tool-result-cell tool-result-cell-highlight">
            <dt>{ui.paybackLabel}</dt>
            <dd>{paybackText}</dd>
          </div>
          <div className="tool-result-cell tool-result-cell-highlight">
            <dt>{ui.roiLabel}</dt>
            <dd>{roiText}</dd>
          </div>
        </dl>

        <p className="tool-hint">
          {ui.basisNote.replace(
            '{weeks}',
            oneDecimal.format(WEEKS_PER_MONTH),
          )}
        </p>
      </div>

      <p className="tool-disclaimer">
        {ui.disclaimer}{' '}
        <a className="tool-inline-link" href={guideHref}>
          {ui.guideLink}
        </a>
        {', '}
        <a className="tool-inline-link" href={comparisonHref}>
          {ui.comparisonLink}
        </a>
        {ui.linkSeparator}
        <a className="tool-inline-link" href={glossaryHref}>
          {ui.glossaryLink}
        </a>
        {'.'}
      </p>
    </div>
  )
}
